import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { saveProcessoArbitralDraftSnapshot } from '../rascunhoStep/actions';
import {
  type RecusaDoUsoDocumentoPadrao,
  recusaDoUsoDocumentoPadrao,
} from '@/features/documentosPadrao';
import type { AIContext, PedidoSuggestion } from '@/features/processo-arbitral/types/conflictTypes';
import type { ContactData } from '@/features/processo-arbitral/types/contactTypes';
import type { CustasPorRito } from '@/features/processo-arbitral/types/custasTypes';
import type {
  ConflictDocumentSubtipo,
  DestinoDocumentoPadraoNoCadastro,
  DocumentoPadraoNoRascunho,
  ProcessoArbitralDraftDocumentItem,
  ProcessoArbitralDraftUploadItem,
  RemovedDocumentReference,
  UploadFailure,
  UploadResult,
  UsarDocumentoPadraoNoRascunhoPayload,
} from '@/features/processo-arbitral/types/documentsTypes';
import type {
  ProcessoArbitralDraftSnapshot,
  ProcessoArbitralDraftStep,
} from '@/features/processo-arbitral/types/rascunhoStepTypes';
import {
  destinoNoRascunhoParaApi,
  subtipoDoComprovante,
} from '@/features/processo-arbitral/utils/documentoPadraoNoCadastroUtils';
import { normalizeProcessoArbitralDraftSnapshot } from '@/features/processo-arbitral/utils/rascunhoStepUtils';
import type { RootState } from '@/store';

import { wizardStepService } from './services';

interface SaveWizardDraftInput {
  ultimoPasso?: ProcessoArbitralDraftStep;
  snapshot?: Partial<ProcessoArbitralDraftSnapshot>;
  silent?: boolean;
}

interface HydratedWizardDraftData {
  snapshot: ProcessoArbitralDraftSnapshot;
  updatedAt: string | null;
}

interface SavedWizardDraftData {
  updatedAt: string | null;
}

interface SyncWizardDraftDocumentsPayload {
  draftId: string;
  documents: ProcessoArbitralDraftDocumentItem[];
  uploads: ProcessoArbitralDraftUploadItem[];
}

interface RemoveProofDocumentPayload {
  requirementId: string;
  removed: RemovedDocumentReference;
}

interface RemoveConflictDocumentPayload {
  documentId: string;
  removed: RemovedDocumentReference;
}

interface ProofUploadSuccessPayload {
  requirementId: string;
  result: UploadResult;
}

interface ConflictUploadSuccessPayload {
  documentId: string;
  result: UploadResult;
}

interface ProofUploadFailurePayload {
  requirementId: string;
  failure: UploadFailure;
}

interface ConflictUploadFailurePayload {
  documentId: string;
  failure: UploadFailure;
}

const buildWizardSnapshot = (
  state: RootState,
  overrides?: Partial<ProcessoArbitralDraftSnapshot>,
): ProcessoArbitralDraftSnapshot =>
  normalizeProcessoArbitralDraftSnapshot({
    requerentes: overrides?.requerentes ?? state.processoArbitralWizardStep.requerente.parties,
    requeridos: overrides?.requeridos ?? state.processoArbitralWizardStep.requerido.parties,
    conflito: overrides?.conflito ?? state.processoArbitralWizardStep.conflito.formData,
  });

const normalizeUploadFailure = (error: unknown): UploadFailure => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof error.code === 'string' &&
    typeof error.message === 'string'
  ) {
    return {
      code: error.code === 'aborted' ? 'aborted' : 'default',
      message: error.message,
    };
  }

  return {
    code: 'default',
    message: error instanceof Error ? error.message : 'Erro ao enviar arquivo. Tente novamente.',
  };
};

const uploadProof = async (
  requirementId: string,
  file: File,
  getState: () => RootState,
  dispatch: (action: unknown) => void,
): Promise<ProofUploadSuccessPayload> => {
  const state = getState();
  const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;
  const requirement = state.processoArbitralWizardStep.documentos.formData.comprovacao.find(
    (document) => document.id === requirementId,
  );

  if (!draftId) {
    throw new Error('Rascunho arbitral não inicializado');
  }

  if (!requirement) {
    throw new Error('Documento obrigatório não encontrado');
  }

  const result = await wizardStepService.uploadDraftDocument({
    draftId,
    uploadKey: requirementId,
    file,
    categoria: 'comprovacao',
    subtipo: subtipoDoComprovante(requirement.documentType),
    requirementId,
    partyId: requirement.partyId,
    partyName: requirement.partyName,
    partyRole: requirement.partyRole,
    representedSide: requirement.representedSide,
    onProgress: (uploadProgress: number) => {
      dispatch(setProofUploadProgress({ requirementId, uploadProgress }));
    },
  });

  return { requirementId, result };
};

const uploadConflict = async (
  documentId: string,
  file: File,
  getState: () => RootState,
  dispatch: (action: unknown) => void,
): Promise<ConflictUploadSuccessPayload> => {
  const draftId = getState().processoArbitralRascunhoStep.activeDraft?.id;

  if (!draftId) {
    throw new Error('Rascunho arbitral não inicializado');
  }

  const documento = getState().processoArbitralWizardStep.documentos.formData.conflito.find(
    (item) => item.id === documentId,
  );

  const result = await wizardStepService.uploadDraftDocument({
    draftId,
    uploadKey: documentId,
    file,
    categoria: 'conflito',
    // Lido do estado (posto lá pelo `pending`) para o retry preservar a marcação.
    subtipo: documento?.subtipo ?? 'ANEXO_CONFLITO',
    requirementId: documentId,
    onProgress: (uploadProgress: number) => {
      dispatch(setConflictUploadProgress({ documentId, uploadProgress }));
    },
  });

  return { documentId, result };
};

export const setProofUploadProgress = createAction<{
  requirementId: string;
  uploadProgress: number;
}>('processoArbitral/wizardStep/setProofUploadProgress');

export const setConflictUploadProgress = createAction<{
  documentId: string;
  uploadProgress: number;
}>('processoArbitral/wizardStep/setConflictUploadProgress');

export const fetchRequerenteRecentContacts = createAsyncThunk<
  Awaited<ReturnType<typeof wizardStepService.fetchRecentContacts>>,
  void,
  { rejectValue: string; state: RootState }
>(
  'processoArbitral/wizardStep/fetchRequerenteRecentContacts',
  async (_, { rejectWithValue, getState }) => {
    try {
      const user = getState().auth.user;

      if (!user?.empresaId) {
        return rejectWithValue('Não autenticado');
      }

      return await wizardStepService.fetchRecentContacts();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao carregar contatos recentes',
      );
    }
  },
);

export const searchRequerenteContactByDocument = createAsyncThunk<
  Awaited<ReturnType<typeof wizardStepService.searchContactByDocument>>,
  string,
  { rejectValue: string; state: RootState }
>(
  'processoArbitral/wizardStep/searchRequerenteContactByDocument',
  async (documento, { rejectWithValue, getState }) => {
    try {
      const user = getState().auth.user;

      if (!user?.empresaId) {
        return rejectWithValue('Não autenticado');
      }

      return await wizardStepService.searchContactByDocument(documento);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Erro ao buscar contato');
    }
  },
);

export const saveRequerenteCatalogContact = createAsyncThunk<
  Awaited<ReturnType<typeof wizardStepService.saveCatalogContact>>,
  ContactData,
  { rejectValue: string; state: RootState }
>(
  'processoArbitral/wizardStep/saveRequerenteCatalogContact',
  async (contact, { rejectWithValue, getState }) => {
    try {
      const user = getState().auth.user;

      if (!user?.empresaId) {
        return rejectWithValue('Não autenticado');
      }

      return await wizardStepService.saveCatalogContact(contact);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao salvar contato no catálogo',
      );
    }
  },
);

export const searchRequeridoContactByDocument = createAsyncThunk<
  Awaited<ReturnType<typeof wizardStepService.searchContactByDocument>>,
  string,
  { rejectValue: string; state: RootState }
>(
  'processoArbitral/wizardStep/searchRequeridoContactByDocument',
  async (documento, { rejectWithValue, getState }) => {
    try {
      const user = getState().auth.user;

      if (!user?.empresaId) {
        return rejectWithValue('Não autenticado');
      }

      return await wizardStepService.searchContactByDocument(documento);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Erro ao buscar contato');
    }
  },
);

/**
 * Bloqueio de duplicidade na criação: este CPF/CNPJ já é REQUERIDO de um processo arbitral
 * em aberto NESTE escritório? Só o polo requerido consulta — a trava existe para não abrir
 * dois processos contra o mesmo devedor ao mesmo tempo, e o requerente é quem move a ação:
 * o mesmo credor legitimamente abre vários processos contra requeridos diferentes.
 * O endpoint passou a filtrar por papel em core#1633 (ClickUp 86akcd2ft); antes de ajustar
 * o front, a aba Requerente herdava essa resposta e barrava quem fosse requerido em
 * qualquer outro processo aberto.
 */
export const verificarProcessoAbertoPorDocumento = createAsyncThunk<boolean, string>(
  'processoArbitral/wizardStep/verificarProcessoAbertoPorDocumento',
  async (cpfCnpj) => wizardStepService.verificarProcessoAbertoPorDocumento(cpfCnpj),
);

export const fetchCustasPorRito = createAsyncThunk<CustasPorRito, void, { rejectValue: string }>(
  'processoArbitral/wizardStep/fetchCustasPorRito',
  async (_, { rejectWithValue }) => {
    try {
      return await wizardStepService.fetchCustasPorRito();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao carregar as custas da câmara',
      );
    }
  },
);

export const saveRequeridoCatalogContact = createAsyncThunk<
  Awaited<ReturnType<typeof wizardStepService.saveCatalogContact>>,
  ContactData,
  { rejectValue: string; state: RootState }
>(
  'processoArbitral/wizardStep/saveRequeridoCatalogContact',
  async (contact, { rejectWithValue, getState }) => {
    try {
      const user = getState().auth.user;

      if (!user?.empresaId) {
        return rejectWithValue('Não autenticado');
      }

      return await wizardStepService.saveCatalogContact(contact);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao salvar contato no catálogo',
      );
    }
  },
);

export const hydrateWizardDraft = createAsyncThunk<
  HydratedWizardDraftData | null,
  void,
  { state: RootState }
>('processoArbitral/wizardStep/hydrateDraft', async (_, { getState }) => {
  const activeDraft = getState().processoArbitralRascunhoStep.activeDraft;

  if (!activeDraft) {
    return null;
  }

  return {
    snapshot: normalizeProcessoArbitralDraftSnapshot(activeDraft.payload),
    updatedAt: activeDraft.dataAtualizacao,
  };
});

export const syncWizardDraftDocuments = createAsyncThunk<
  SyncWizardDraftDocumentsPayload | null,
  void,
  { state: RootState; rejectValue: string }
>('processoArbitral/wizardStep/syncDraftDocuments', async (_, { getState, rejectWithValue }) => {
  try {
    const draftId = getState().processoArbitralRascunhoStep.activeDraft?.id;

    if (!draftId) {
      return null;
    }

    const [documents, uploads] = await Promise.all([
      wizardStepService.listDraftDocuments(draftId),
      wizardStepService.listDraftDocumentUploads(draftId),
    ]);

    return {
      draftId,
      documents,
      uploads,
    };
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao sincronizar documentos do rascunho',
    );
  }
});

export const saveWizardDraft = createAsyncThunk<
  SavedWizardDraftData,
  SaveWizardDraftInput,
  { rejectValue: string; state: RootState }
>(
  'processoArbitral/wizardStep/saveDraft',
  async ({ ultimoPasso, snapshot }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (!state.processoArbitralWizardStep.isDraftHydrated) {
        return rejectWithValue('Aguarde a hidratação do rascunho antes de salvar.');
      }

      const { draft } = await dispatch(
        saveProcessoArbitralDraftSnapshot({
          snapshot: buildWizardSnapshot(state, snapshot),
          ultimoPasso,
        }),
      ).unwrap();

      return {
        updatedAt: draft.dataAtualizacao,
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Erro ao salvar rascunho');
    }
  },
);

export const generateConflitoAiSuggestions = createAsyncThunk<
  PedidoSuggestion[],
  AIContext,
  { rejectValue: string }
>(
  'processoArbitral/wizardStep/generateConflitoAiSuggestions',
  async (context, { rejectWithValue }) => {
    try {
      return await wizardStepService.generateAiSuggestions(context);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao gerar sugestões de pedido',
      );
    }
  },
);

export const uploadProofDocument = createAsyncThunk<
  ProofUploadSuccessPayload,
  { requirementId: string; file: File },
  { rejectValue: ProofUploadFailurePayload; state: RootState }
>('processoArbitral/wizardStep/uploadProofDocument', async ({ requirementId, file }, thunkApi) => {
  try {
    const result = await uploadProof(requirementId, file, thunkApi.getState, thunkApi.dispatch);
    wizardStepService.forgetUpload(requirementId);

    if (
      await discardUploadCanceladoDurante(
        thunkApi.requestId,
        result.result.uploadId,
        thunkApi.getState,
      )
    ) {
      return thunkApi.rejectWithValue({ requirementId, failure: abortedUploadFailure });
    }

    return result;
  } catch (error) {
    return thunkApi.rejectWithValue({
      requirementId,
      failure: normalizeUploadFailure(error),
    });
  }
});

export const retryProofDocument = createAsyncThunk<
  ProofUploadSuccessPayload,
  { requirementId: string },
  { rejectValue: ProofUploadFailurePayload; state: RootState }
>('processoArbitral/wizardStep/retryProofDocument', async ({ requirementId }, thunkApi) => {
  const file = wizardStepService.getRetryFile(requirementId);

  try {
    if (file) {
      const result = await uploadProof(requirementId, file, thunkApi.getState, thunkApi.dispatch);
      wizardStepService.forgetUpload(requirementId);
      return result;
    }

    const state = thunkApi.getState();
    const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;
    const document = state.processoArbitralWizardStep.documentos.formData.comprovacao.find(
      (item) => item.id === requirementId,
    );

    if (!draftId || !document?.uploadId) {
      return thunkApi.rejectWithValue({
        requirementId,
        failure: {
          code: 'default',
          message: 'Não foi possível reenfileirar o upload do documento.',
        },
      });
    }

    const upload = await wizardStepService.retryDraftUpload(draftId, document.uploadId);

    return {
      requirementId,
      result: {
        uploadId: upload.id,
        status: upload.status,
        fileId: upload.documentoId,
        fileName: upload.nomeOriginal || upload.nomeArquivo,
        fileSize: upload.tamanhoArquivo,
        mimeType: upload.tipoMime || undefined,
        error: upload.mensagemErro,
      },
    };
  } catch (error) {
    wizardStepService.forgetUpload(requirementId);
    return thunkApi.rejectWithValue({
      requirementId,
      failure: normalizeUploadFailure(error),
    });
  }
});

export const uploadConflictDocument = createAsyncThunk<
  ConflictUploadSuccessPayload,
  { documentId: string; file: File; subtipo?: ConflictDocumentSubtipo },
  { rejectValue: ConflictUploadFailurePayload; state: RootState }
>('processoArbitral/wizardStep/uploadConflictDocument', async ({ documentId, file }, thunkApi) => {
  try {
    const result = await uploadConflict(documentId, file, thunkApi.getState, thunkApi.dispatch);
    wizardStepService.forgetUpload(documentId);

    if (
      await discardUploadCanceladoDurante(
        thunkApi.requestId,
        result.result.uploadId,
        thunkApi.getState,
      )
    ) {
      return thunkApi.rejectWithValue({ documentId, failure: abortedUploadFailure });
    }

    return result;
  } catch (error) {
    return thunkApi.rejectWithValue({
      documentId,
      failure: normalizeUploadFailure(error),
    });
  }
});

export const retryConflictDocument = createAsyncThunk<
  ConflictUploadSuccessPayload,
  { documentId: string },
  { rejectValue: ConflictUploadFailurePayload; state: RootState }
>('processoArbitral/wizardStep/retryConflictDocument', async ({ documentId }, thunkApi) => {
  const file = wizardStepService.getRetryFile(documentId);

  try {
    if (file) {
      const result = await uploadConflict(documentId, file, thunkApi.getState, thunkApi.dispatch);
      wizardStepService.forgetUpload(documentId);
      return result;
    }

    const state = thunkApi.getState();
    const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;
    const document = state.processoArbitralWizardStep.documentos.formData.conflito.find(
      (item) => item.id === documentId,
    );

    if (!draftId || !document?.uploadId) {
      return thunkApi.rejectWithValue({
        documentId,
        failure: {
          code: 'default',
          message: 'Não foi possível reenfileirar o upload do documento.',
        },
      });
    }

    const upload = await wizardStepService.retryDraftUpload(draftId, document.uploadId);

    return {
      documentId,
      result: {
        uploadId: upload.id,
        status: upload.status,
        fileId: upload.documentoId,
        fileName: upload.nomeOriginal || upload.nomeArquivo,
        fileSize: upload.tamanhoArquivo,
        mimeType: upload.tipoMime || undefined,
        error: upload.mensagemErro,
      },
    };
  } catch (error) {
    wizardStepService.forgetUpload(documentId);
    return thunkApi.rejectWithValue({
      documentId,
      failure: normalizeUploadFailure(error),
    });
  }
});

/**
 * Tira o anexo do backend antes de tirá-lo da tela.
 *
 * O documento materializado sai por `removeDraftDocument`; o upload que ainda
 * não virou documento precisa de `cancelDraftUpload`, senão ele continua na
 * listagem de uploads do rascunho — e o polling do wizard devolvia o item
 * removido à lista segundos depois.
 */
const removeDraftAttachment = async (
  requirementId: string,
  draftId: string | undefined,
  document: { fileId: string | null; uploadId: string | null; attemptId?: string } | undefined,
): Promise<RemovedDocumentReference> => {
  if (!draftId || !document) {
    return { requirementId, fileId: null, uploadId: null, attemptId: null };
  }

  if (document.fileId) {
    await wizardStepService.removeDraftDocument(draftId, document.fileId);
  } else if (document.uploadId) {
    await wizardStepService.cancelDraftUpload(draftId, document.uploadId);
  }

  return {
    requirementId,
    fileId: document.fileId,
    uploadId: document.uploadId,
    attemptId: document.attemptId ?? null,
  };
};

/**
 * Desfaz no backend um upload que terminou depois de o usuário já ter removido o
 * item da lista.
 *
 * O envio não é abortável no meio (o POST vai inteiro), então o cancelamento
 * durante o upload só some com o card: sem esta compensação o anexo continuaria
 * vinculado ao rascunho e voltaria à tela no primeiro polling.
 *
 * A chave é a TENTATIVA (`requestId` do thunk), não o requisito: o
 * `requirementId` de comprovação é estável, então reanexar no mesmo requisito
 * apagava o registro de cancelamento do envio anterior e desligava esta
 * compensação justo para ele.
 */
const discardUploadCanceladoDurante = async (
  attemptId: string,
  uploadId: string,
  getState: () => RootState,
): Promise<boolean> => {
  const state = getState();
  const foiRemovido =
    state.processoArbitralWizardStep.documentos.removed.attemptIds.includes(attemptId);

  if (!foiRemovido) {
    return false;
  }

  const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;

  if (draftId) {
    await wizardStepService.cancelDraftUpload(draftId, uploadId);
  }

  return true;
};

const abortedUploadFailure: UploadFailure = {
  code: 'aborted',
  message: 'Upload cancelado.',
};

export const removeProofDocument = createAsyncThunk<
  RemoveProofDocumentPayload,
  { requirementId: string },
  { rejectValue: ProofUploadFailurePayload; state: RootState }
>('processoArbitral/wizardStep/removeProofDocument', async ({ requirementId }, thunkApi) => {
  try {
    const state = thunkApi.getState();
    const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;
    const document = state.processoArbitralWizardStep.documentos.formData.comprovacao.find(
      (item) => item.id === requirementId,
    );

    wizardStepService.cancelUpload(requirementId);
    const removed = await removeDraftAttachment(requirementId, draftId, document);

    wizardStepService.forgetUpload(requirementId);
    return { requirementId, removed };
  } catch (error) {
    return thunkApi.rejectWithValue({
      requirementId,
      failure: normalizeUploadFailure(error),
    });
  }
});

export const removeConflictDocument = createAsyncThunk<
  RemoveConflictDocumentPayload,
  { documentId: string },
  { rejectValue: ConflictUploadFailurePayload; state: RootState }
>('processoArbitral/wizardStep/removeConflictDocument', async ({ documentId }, thunkApi) => {
  try {
    const state = thunkApi.getState();
    const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;
    const document = state.processoArbitralWizardStep.documentos.formData.conflito.find(
      (item) => item.id === documentId,
    );

    wizardStepService.cancelUpload(documentId);
    const removed = await removeDraftAttachment(documentId, draftId, document);

    wizardStepService.forgetUpload(documentId);
    return { documentId, removed };
  } catch (error) {
    return thunkApi.rejectWithValue({
      documentId,
      failure: normalizeUploadFailure(error),
    });
  }
});

/** As frases que só o rascunho tem; as da biblioteca e dos status vêm de `recusaDoUsoDocumentoPadrao`. */
const MENSAGENS_DO_USO_NO_RASCUNHO: Record<string, string> = {
  DOCUMENTO_PADRAO_JA_JUNTADO: 'Esta versão do documento já está nos documentos deste cadastro.',
  PETICAO_INICIAL_JA_ANEXADA:
    'Este cadastro já tem uma petição inicial. Remova a atual para usar outra.',
  COMPROVANTE_JA_ANEXADO: 'Este comprovante já tem um arquivo. Remova o atual para usar outro.',
};

/**
 * Recusa decidida no front. O `status` é o que a chave do `useUsoDocumentoPadraoComChave` lê: 4xx
 * troca a chave na próxima tentativa, e `null` a mantém, para repetir virar replay no core.
 */
const recusaLocal = (
  mensagem: string,
  status: number | null = 400,
): RecusaDoUsoDocumentoPadrao => ({
  mensagem,
  status,
  codigo: null,
  listaDesatualizada: false,
});

/**
 * O que confirma o uso para a pessoa é o documento na etapa, não só o 2xx do core:
 * - replay de um uso cujo documento já foi tirado do cadastro → nada a mostrar; recusa definitiva,
 *   e a próxima confirmação, com chave nova, anexa de novo;
 * - a sincronização da etapa falhou, ou voltou sem o documento → ele está no rascunho mas não na
 *   tela; recusa sem status, e repetir com a mesma chave vira replay e sincroniza de novo.
 */
const RECUSA_DO_DOCUMENTO_REMOVIDO = recusaLocal(
  'Este documento foi tirado do cadastro depois de anexado. Confirme de novo para anexá-lo outra vez.',
  409,
);
const RECUSA_DA_ETAPA_DESATUALIZADA = recusaLocal(
  'O documento foi anexado, mas a etapa não atualizou. Confirme de novo para recarregá-la.',
  null,
);

/**
 * Uso de documento padrão na etapa Documentos do cadastro. Resolve só com o documento persistido no
 * rascunho e a etapa já sincronizada: o painel fecha no `fulfilled`, e o arquivo aparece no destino
 * pelo mesmo caminho do upload local (`syncWizardDraftDocuments`), com a validação da etapa
 * recalculada a partir dele.
 */
export const usarDocumentoPadraoNoRascunho = createAsyncThunk<
  DocumentoPadraoNoRascunho,
  Omit<UsarDocumentoPadraoNoRascunhoPayload, 'destino'> & {
    destino: DestinoDocumentoPadraoNoCadastro;
  },
  { rejectValue: RecusaDoUsoDocumentoPadrao; state: RootState }
>(
  'processoArbitral/wizardStep/usarDocumentoPadraoNoRascunho',
  async ({ destino, ...payload }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;
    if (!draftId) {
      return rejectWithValue(recusaLocal('Aguarde a inicialização do rascunho e tente novamente.'));
    }
    const destinoNaApi = destinoNoRascunhoParaApi(
      destino,
      state.processoArbitralWizardStep.documentos.formData.comprovacao,
    );
    if (!destinoNaApi) {
      return rejectWithValue(
        recusaLocal('Este comprovante não é mais exigido na etapa. Feche e escolha de novo.'),
      );
    }

    let usado: DocumentoPadraoNoRascunho;
    try {
      usado = await wizardStepService.usarDocumentoPadraoNoRascunho(draftId, {
        ...payload,
        destino: destinoNaApi,
      });
    } catch (erro) {
      return rejectWithValue(recusaDoUsoDocumentoPadrao(erro, MENSAGENS_DO_USO_NO_RASCUNHO));
    }
    if (usado.documentoId === null) return rejectWithValue(RECUSA_DO_DOCUMENTO_REMOVIDO);
    const sincronizado = await dispatch(syncWizardDraftDocuments());
    if (!etapaTrazODocumento(sincronizado, usado.documentoId)) {
      return rejectWithValue(RECUSA_DA_ETAPA_DESATUALIZADA);
    }
    return usado;
  },
);

/**
 * A sincronização terminou E trouxe o documento que o core acabou de gravar. Terminar não basta:
 * uma listagem que não o traga deixaria o painel fechar com nada novo na etapa.
 */
function etapaTrazODocumento(
  sincronizado: Awaited<ReturnType<ReturnType<typeof syncWizardDraftDocuments>>>,
  documentoId: string,
): boolean {
  return (
    syncWizardDraftDocuments.fulfilled.match(sincronizado) &&
    sincronizado.payload !== null &&
    sincronizado.payload.documents.some((documento) => documento.id === documentoId)
  );
}
